/**
 * Entregas privadas a clientes.
 *
 * Cada entrega es una página interna: no aparece en la navegación, no entra
 * al sitemap (scripts/sitemap.mjs solo lista rutas fijas y segmentos) y lleva
 * noindex por meta y por cabecera (public/_headers). Se llega a ella solo con
 * el enlace que se le manda al cliente.
 *
 * NO SE MEZCLA CON EL PORTAFOLIO. Estas fotos son de un encargo privado: no
 * van a data/portafolio.js salvo que el cliente firme la autorización de uso
 * de imagen (docs/autorizacion-uso-imagen.md).
 *
 * CON CADA SESIÓN se sigue docs/proceso-entrega.md: crear la entrega, mandar
 * el enlace, conseguir la firma en /entregas/<evento>/autorizacion (esa ruta
 * se crea sola por cada entrada de esta lista) y revisarla antes de publicar.
 *
 * Las imágenes se generan igual que las del portafolio — WebP 480/960/1600 con
 * ffmpeg y `-map_metadata -1` — porque el EXIF de un evento privado lleva la
 * fecha y a veces la ubicación de la casa.
 *
 * Campos por entrega:
 *   path       ruta pública, siempre bajo /entregas/
 *   titulo     lo que se lee en el hero
 *   fecha      fecha del evento, en texto
 *   portada    foto del hero
 *   fotos      muestra de la galería, en el orden de la historia
 *
 * LA DESCARGA NO VA AQUÍ. El enlace a la carpeta de Drive y la clave de cada
 * entrega viven en Supabase (omega_entregas_privadas) y solo salen con la
 * clave correcta (netlify/functions/descarga.js). Este archivo termina en el
 * JavaScript público del sitio: un enlace puesto aquí lo puede ver cualquiera.
 * Cómo darle clave y carpeta a una entrega nueva: docs/proceso-entrega.md.
 *
 * La galería va en dos columnas y `ancha` ocupa la fila completa. Se marca a
 * mano, no por posición: lo que va a lo ancho es un momento con emoción, no
 * lo que caiga en el quinto lugar. Entre dos anchas, un número par de fotos,
 * para que ninguna fila quede coja.
 */

/** Constructor de fotos de una entrega, a partir de su carpeta en public/. */
const fotosEn = (carpeta) => (id, w, h, alt, { ancha = false } = {}) => ({
  id,
  src: `${carpeta}/${id}-960.webp`,
  srcSet: [480, 960, 1600].map((ancho) => `${carpeta}/${id}-${ancho}.webp ${ancho}w`).join(", "),
  w,
  h,
  ratio: Number((h / w).toFixed(4)),
  alt,
  ancha,
});

const foto = fotosEn("/fotografia/entregas/boda-26-09-2026");
const fotoEmbarazo = fotosEn("/fotografia/entregas/revelacion-embarazo-17-01-2026");
const fotoGenero = fotosEn("/fotografia/entregas/revelacion-sexo-21-03-2026");
const fotoBabyShower = fotosEn("/fotografia/entregas/baby-shower-06-06-2026");
const fotoCumple = fotosEn("/fotografia/entregas/cumpleanos-18-08-10-2026");
const fotoGeneroNina = fotosEn("/fotografia/entregas/revelacion-sexo-10-10-2026");
const fotoGeneroNino = fotosEn("/fotografia/entregas/revelacion-sexo-11-10-2026");
const fotoCumpleOctubre = fotosEn("/fotografia/entregas/cumpleanos-19-10-2026");
const fotoGeneroNoviembre = fotosEn("/fotografia/entregas/revelacion-sexo-01-11-2026");
const fotoFiestas = fotosEn("/fotografia/entregas/fiestas-patrias-11-09-2026");

export const ENTREGAS = [
  {
    path: "/entregas/revelacion-sexo-01-11-2026",
    titulo: "Revelación de sexo",
    fecha: "01 · 11 · 2026",
    portada: fotoGeneroNoviembre("genero-00", 4388, 2925, "El humo rosado estalla sobre las letras OH BABY entre las chispas, mientras los futuros papás presionan el detonador: es niña"),
    fotos: [
      fotoGeneroNoviembre("genero-01", 5184, 3456, "Los futuros papás, vestidos de blanco, posan con las manos sobre la barriga frente a los globos blancos y dorados"),
      fotoGeneroNoviembre("genero-02", 5184, 3456, "La futura mamá, con corona y vestido blanco, mira su barriga junto a las letras BABY"),
      fotoGeneroNoviembre("genero-03", 5184, 3456, "Las manos de los dos forman un corazón sobre la barriga", { ancha: true }),
      fotoGeneroNoviembre("genero-04", 5184, 3456, "Él, arrodillado, besa la barriga de la futura mamá"),
      fotoGeneroNoviembre("genero-05", 5184, 3456, "Él le acomoda la corona a la futura mamá, que sonríe con las manos en la barriga"),
      fotoGeneroNoviembre("genero-06", 4637, 3091, "La familia ríe frente a las letras OH BABY, con los dos más pequeños en brazos", { ancha: true }),
      fotoGeneroNoviembre("genero-07", 5184, 3456, "Los futuros papás, de la mano, esperan junto al detonador TNT antes de la revelación"),
      fotoGeneroNoviembre("genero-09", 4106, 2737, "Las chispas se encienden y el humo rosado empieza a cubrir los globos"),
      fotoGeneroNoviembre("genero-08", 5184, 3456, "Los futuros papás se abrazan con fuerza, con el humo rosado de fondo", { ancha: true }),
      fotoGeneroNoviembre("genero-10", 2812, 1875, "Él, emocionado, besa a la futura mamá tras la revelación"),
      fotoGeneroNoviembre("genero-11", 4321, 2881, "Una familiar abraza fuerte a la futura mamá"),
      fotoGeneroNoviembre("genero-12", 5184, 3456, "Todos los invitados al anochecer, frente a las letras iluminadas", { ancha: true }),
      fotoGeneroNoviembre("genero-13", 5184, 3456, "La futura mamá sostiene su torta con las velas encendidas frente a las letras luminosas"),
      fotoGeneroNoviembre("genero-14", 5184, 3456, "La futura mamá y un joven sonríen con la torta entre las letras iluminadas"),
      fotoGeneroNoviembre("genero-15", 5184, 3456, "La futura mamá sostiene la torta en alto y muestra su barriga entre los globos dorados", { ancha: true }),
    ],
  },
  {
    path: "/entregas/cumpleanos-19-10-2026",
    titulo: "Cumpleaños",
    fecha: "19 · 10 · 2026",
    portada: fotoCumpleOctubre("cumple-00", 3084, 2056, "La cumpleañera, con lentes de «Happy Birthday», sopla las velas doradas de su torta iluminada por las llamas"),
    fotos: [
      fotoCumpleOctubre("cumple-01", 5184, 3456, "La torta de chocolate con velas doradas sobre la mesa, junto al espumante, el gorro de cumpleaños y un ramo de flores"),
      fotoCumpleOctubre("cumple-02", 5184, 3456, "La cumpleañera posa con una mano en la cintura frente al telón dorado y el cartel de feliz cumpleaños"),
      fotoCumpleOctubre("cumple-03", 5184, 3456, "La cumpleañera ríe con el brazo en alto frente al telón dorado", { ancha: true }),
      fotoCumpleOctubre("cumple-04", 4443, 2962, "La cumpleañera sostiene su torta con las velas doradas y la mira sonriendo"),
      fotoCumpleOctubre("cumple-05", 5184, 3456, "La cumpleañera muestra la torta de chocolate a cámara"),
      fotoCumpleOctubre("cumple-06", 3956, 2637, "Seis amigas abrazadas frente al telón dorado"),
      fotoCumpleOctubre("cumple-07", 5184, 3456, "La cumpleañera y su pareja se besan frente al telón dorado"),
      fotoCumpleOctubre("cumple-08", 5184, 3456, "La cumpleañera, con lentes de «Happy Birthday», levanta la botella de espumante", { ancha: true }),
      fotoCumpleOctubre("cumple-09", 5184, 3456, "La cumpleañera sostiene un ramo de flores junto a un invitado con gorra"),
      fotoCumpleOctubre("cumple-10", 5184, 3456, "Una invitada con polerón granate baila con los brazos abiertos en la pista"),
      fotoCumpleOctubre("cumple-11", 3293, 2195, "Tres invitadas ríen juntas, una de ellas con gorro de cumpleaños"),
      fotoCumpleOctubre("cumple-12", 5184, 3456, "La cumpleañera ríe abrazada a una invitada mientras sostienen un regalo"),
      fotoCumpleOctubre("cumple-13", 4116, 2744, "La cumpleañera sonríe tras las velas encendidas de su torta", { ancha: true }),
      fotoCumpleOctubre("cumple-14", 3460, 2307, "La cumpleañera, con la botella de espumante en las manos, entre la lluvia de confeti"),
      fotoCumpleOctubre("cumple-15", 5184, 3456, "Una pareja de invitados brinda con copas frente al telón dorado"),
      fotoCumpleOctubre("cumple-16", 4432, 2955, "La cumpleañera y su pareja ríen bajo la lluvia de confeti junto a la torta", { ancha: true }),
    ],
  },
  {
    path: "/entregas/revelacion-sexo-11-10-2026",
    titulo: "Revelación de sexo",
    fecha: "11 · 10 · 2026",
    portada: fotoGeneroNino("genero-00", 5184, 3456, "Él celebra con el puño en alto y la futura mamá se tapa la boca de la emoción mientras el humo azul revela que es niño"),
    fotos: [
      fotoGeneroNino("genero-01", 4608, 3456, "Los futuros papás se miran sonriendo junto a la laguna, con las manos de ambos sobre la barriga"),
      fotoGeneroNino("genero-02", 4608, 3456, "La futura mamá, con vestido de encaje, se toma la barriga y mira hacia la laguna"),
      fotoGeneroNino("genero-03", 5184, 3456, "Los futuros papás caminan de la mano por un sendero entre árboles y cercos blancos", { ancha: true }),
      fotoGeneroNino("genero-04", 4608, 3456, "Él, arrodillado en el muelle, besa la barriga de la futura mamá"),
      fotoGeneroNino("genero-05", 4490, 3368, "Las manos de los dos forman un corazón sobre la barriga"),
      fotoGeneroNino("genero-06", 5184, 3456, "Los futuros papás se besan en el sendero, con las manos sobre la barriga", { ancha: true }),
      fotoGeneroNino("genero-07", 5184, 3456, "Letras luminosas OH BABY con globos celestes, rosados y dorados, entre chispas y el detonador al centro"),
      fotoGeneroNino("genero-08", 5184, 3456, "Los futuros papás sonríen junto al detonador frente a las letras OH BABY"),
      fotoGeneroNino("genero-09", 4216, 2811, "El humo azul se eleva sobre las letras OH BABY mientras los futuros papás presionan el detonador", { ancha: true }),
      fotoGeneroNino("genero-10", 4748, 3165, "Él ríe mirando a la futura mamá, que se cubre el rostro emocionada"),
      fotoGeneroNino("genero-11", 5184, 3456, "Una familiar posa la mano sobre la barriga de la futura mamá frente a las letras luminosas"),
      fotoGeneroNino("genero-12", 5184, 3456, "Los futuros papás se abrazan riendo, con el humo azul de fondo", { ancha: true }),
    ],
  },
  {
    path: "/entregas/revelacion-sexo-10-10-2026",
    titulo: "Revelación de sexo",
    fecha: "10 · 10 · 2026",
    portada: fotoGeneroNina("genero-00", 4156, 2771, "Lluvia de confeti rosado sobre los futuros papás en el momento en que se revela que es niña"),
    fotos: [
      fotoGeneroNina("genero-01", 5184, 3456, "Él abraza por detrás a la futura mamá, que lleva la banda rosada, frente a los globos blancos y dorados"),
      fotoGeneroNina("genero-02", 5184, 3456, "La futura mamá sonríe a cámara con las manos sobre la barriga"),
      fotoGeneroNina("genero-03", 5184, 3456, "Los futuros papás se besan dentro del marco «¿Niña o niño?»", { ancha: true }),
      fotoGeneroNina("genero-04", 5184, 3456, "Él, arrodillado, besa la barriga de la futura mamá frente a las letras luminosas BABY"),
      fotoGeneroNina("genero-05", 5184, 3456, "Las manos de los dos forman un corazón sobre la barriga"),
      fotoGeneroNina("genero-06", 5184, 3456, "Los futuros papás presionan juntos el detonador entre las chispas", { ancha: true }),
      fotoGeneroNina("genero-07", 5184, 3456, "Una familiar abraza a la futura mamá, que ríe a carcajadas"),
      fotoGeneroNina("genero-08", 5184, 3456, "Los futuros papás se abrazan con fuerza entre las chispas tras la revelación"),
      fotoGeneroNina("genero-09", 5184, 3456, "Beso de los futuros papás bajo las chispas, con confeti rosado en el pelo", { ancha: true }),
    ],
  },
  {
    path: "/entregas/cumpleanos-18-08-10-2026",
    titulo: "Cumpleaños 18",
    fecha: "08 · 10 · 2026",
    portada: fotoCumple("cumple-00", 5184, 3456, "El cumpleañero sostiene su torta junto a los globos dorados del 18"),
    fotos: [
      fotoCumple("cumple-01", 5184, 3456, "El cumpleañero sentado entre los globos dorados de «feliz cumple» y el 18"),
      fotoCumple("cumple-09", 4560, 3040, "El cumpleañero con camiseta azul de fútbol se cubre el rostro con las manos y mira entre los dedos"),
      fotoCumple("cumple-10", 4368, 2912, "El cumpleañero y un familiar, ambos con camiseta azul de fútbol, chocan los puños", { ancha: true }),
      fotoCumple("cumple-04", 5184, 3456, "El cumpleañero abraza por detrás a una familiar que sostiene los globos del 18"),
      fotoCumple("cumple-05", 5184, 3456, "El cumpleañero con camiseta azul de fútbol muestra su torta a cámara"),
      fotoCumple("cumple-06", 4784, 3189, "Toda la familia reunida, varios con camisetas de fútbol azules", { ancha: true }),
    ],
  },
  {
    path: "/entregas/boda-26-09-2026",
    titulo: "Boda civil",
    fecha: "26 · 09 · 2026",
    portada: foto("boda-00", 5184, 3456, "Los novios se besan al terminar la ceremonia, con los invitados detrás"),
    fotos: [
      foto("boda-01", 5184, 3456, "Arreglo de flores blancas sobre la mesa de la ceremonia"),
      foto("boda-02", 5184, 3456, "Retrato de la novia con vestido crema sobre fondo blanco"),
      foto("boda-03", 5184, 3456, "Los novios frente a frente, a punto de besarse", { ancha: true }),
      foto("boda-04", 3456, 2304, "La novia abraza al novio y mira a cámara"),
      foto("boda-05", 4808, 3205, "La novia junto a dos jóvenes, abrazadas"),
      foto("boda-06", 5184, 3456, "La novia entra del brazo de un joven de traje"),
      foto("boda-07", 5184, 3456, "Los novios sonríen durante la ceremonia"),
      foto("boda-09", 5184, 3456, "La novia emocionada durante la ceremonia", { ancha: true }),
      foto("boda-08", 4406, 2937, "El novio ríe durante la ceremonia"),
      foto("boda-10", 5184, 3456, "Las manos de los novios muestran los anillos"),
      foto("boda-11", 5184, 3456, "Los novios muestran la libreta de matrimonio", { ancha: true }),
      foto("boda-12", 4320, 2880, "Los novios con tres niños frente al fondo de la ceremonia"),
      foto("boda-13", 4813, 3209, "Torta de bodas con rosas blancas e iniciales doradas"),
    ],
  },
  {
    path: "/entregas/fiestas-patrias-11-09-2026",
    titulo: "Fiestas Patrias",
    fecha: "11 · 09 · 2026",
    portada: fotoFiestas("fiestas-00", 5184, 3456, "Las dos parejas del grupo de baile posan con las manos en la cintura y en alto, ellas con vestidos de flores moradas y ellos de sombrero de huaso"),
    fotos: [
      fotoFiestas("fiestas-01", 5184, 3456, "Una pareja de cueca posa del brazo: ella con vestido rojo de flores y él con camisa rosada, sombrero y pañuelo"),
      fotoFiestas("fiestas-02", 5184, 3456, "Una bailarina de vestido morado y un huaso joven de sombrero sonríen a cámara"),
      fotoFiestas("fiestas-03", 5184, 3456, "Los músicos tocan junto al muro decorado: la cantante con vestido de china, el guitarrista y el acordeonista", { ancha: true }),
      fotoFiestas("fiestas-04", 5184, 3456, "Una bailarina de vestido morado entre dos huasos, con las manos en la cintura, esperando la música"),
      fotoFiestas("fiestas-05", 5184, 3456, "Dos bailarinas y un huaso listos para bailar junto al acordeonista"),
      fotoFiestas("fiestas-06", 5184, 3456, "Dos parejas bailan cueca con los pañuelos en alto frente al público que graba con sus celulares", { ancha: true }),
      fotoFiestas("fiestas-07", 5184, 3456, "Un huaso zapatea con el pañuelo en alto mientras la bailarina hace girar su falda frente a los músicos"),
      fotoFiestas("fiestas-08", 5184, 3456, "Un huaso persigue a la bailarina con el pañuelo, entre el público"),
      fotoFiestas("fiestas-09", 5184, 3456, "Las dos parejas giran tomadas de la mano con las faldas moradas al vuelo", { ancha: true }),
      fotoFiestas("fiestas-10", 5184, 3456, "Un huaso hace girar a la bailarina, que ríe con la mano en alto"),
      fotoFiestas("fiestas-11", 5184, 3456, "Un huaso de chamanto zapatea junto a una bailarina bajo las carpas del asado"),
      fotoFiestas("fiestas-12", 5184, 3456, "Dos bailarinas avanzan juntas con los pañuelos blancos, las faldas abiertas", { ancha: true }),
      fotoFiestas("fiestas-13", 5184, 3456, "Una bailarina levanta el pañuelo y mira a cámara, con el acordeonista de fondo"),
      fotoFiestas("fiestas-14", 5184, 3456, "Una bailarina ríe con el pañuelo en alto y la falda morada en movimiento"),
      fotoFiestas("fiestas-15", 5184, 3456, "Dos huasos zapatean frente a frente con los pañuelos, inclinados hacia el suelo", { ancha: true }),
      fotoFiestas("fiestas-16", 5184, 3456, "Un invitado de poncho baila abrazado con una bailarina del grupo"),
      fotoFiestas("fiestas-17", 5184, 3456, "La cantante de vestido de china baila cueca con un invitado de camisa a cuadros"),
      fotoFiestas("fiestas-18", 5184, 3456, "Un huaso del grupo baila con las invitadas, que levantan los brazos riendo", { ancha: true }),
      fotoFiestas("fiestas-19", 5184, 3456, "Una bailarina de vestido rojo baila con un invitado de chaqueta azul"),
      fotoFiestas("fiestas-20", 5184, 3456, "Un huaso sonríe con el pañuelo en alto, con la cantante de fondo"),
      fotoFiestas("fiestas-21", 5184, 3456, "El grupo completo al cierre: músicos y bailarines frente al muro decorado con escarapelas", { ancha: true }),
    ],
  },
  {
    path: "/entregas/revelacion-embarazo-17-01-2026",
    titulo: "Revelación de embarazo",
    fecha: "17 · 01 · 2026",
    portada: fotoEmbarazo("embarazo-00", 5184, 3456, "La pareja se mira sonriendo tras un periódico que anuncia la llegada del bebé"),
    fotos: [
      fotoEmbarazo("embarazo-01", 3802, 3456, "Ella sostiene la ecografía sobre la barriga mientras él la abraza desde atrás"),
      fotoEmbarazo("embarazo-02", 3802, 3456, "Ella abraza a su pareja por la espalda con la ecografía y unos calcetines de bebé en las manos"),
      fotoEmbarazo("embarazo-03", 5184, 3456, "La pareja se besa mientras muestra la ecografía a cámara", { ancha: true }),
      fotoEmbarazo("embarazo-04", 3802, 3456, "La pareja se mira sonriendo y sostiene la ecografía entre los dos"),
      fotoEmbarazo("embarazo-05", 3802, 3456, "La pareja se toma de la mano con unos calcetines de bebé colgando"),
      fotoEmbarazo("embarazo-06", 5184, 3456, "La pareja asoma sonriendo por encima del periódico «Nuevas noticias»", { ancha: true }),
    ],
  },
  {
    path: "/entregas/revelacion-sexo-21-03-2026",
    titulo: "Revelación de sexo",
    fecha: "21 · 03 · 2026",
    portada: fotoGenero("genero-00", 5184, 3456, "Lluvia de confeti azul en el momento en que se revela que es niño"),
    fotos: [
      fotoGenero("genero-01", 5184, 3456, "Mesa de recuerdos con un conejo de peluche y pulseras rosadas y celestes"),
      fotoGenero("genero-02", 5184, 3456, "La futura mamá pega un sticker celeste en el tablero Team Niño o Team Niña"),
      fotoGenero("genero-03", 3456, 5184, "Arco de globos pastel, un oso de peluche y cubos con la palabra BABY"),
      fotoGenero("genero-04", 3456, 5184, "Los futuros papás abrazados junto a la torta frente al letrero Girl or Boy"),
      fotoGenero("genero-05", 5184, 3456, "Los futuros papás sonríen a cámara junto al tablero de votos", { ancha: true }),
      fotoGenero("genero-06", 5184, 3456, "Dos invitadas apuntan los lanzadores de confeti mientras los papás esperan junto a la torta"),
      fotoGenero("genero-07", 5184, 3456, "Brindis de los papás entre el humo azul, con la familia conectada en la pantalla"),
    ],
  },
  {
    path: "/entregas/baby-shower-06-06-2026",
    titulo: "Baby shower",
    fecha: "06 · 06 · 2026",
    portada: fotoBabyShower("babyshower-00", 5184, 3456, "El papá besa en la mejilla a la mamá, que se toma la barriga frente a los globos azules"),
    fotos: [
      fotoBabyShower("babyshower-01", 5184, 3456, "Los futuros papás llegan riendo a la celebración"),
      fotoBabyShower("babyshower-02", 5184, 3456, "Mesa de postres con cupcakes y vasitos de crema decorados en celeste"),
      fotoBabyShower("babyshower-03", 5184, 3456, "La mamá con los carteles «Se agranda la familia» y «Me muero por conocerte», rodeada de su familia", { ancha: true }),
      fotoBabyShower("babyshower-04", 5184, 3456, "Una amiga abraza la barriga de la mamá con los ojos cerrados"),
      fotoBabyShower("babyshower-05", 5184, 3456, "Los invitados miden la barriga de la mamá con una lana en el juego de la pancita"),
      fotoBabyShower("babyshower-06", 5184, 3456, "Los invitados lanzan un muñeco al aire con una sábana durante un juego"),
      fotoBabyShower("babyshower-07", 5184, 3456, "La mamá con sus amigos y los carteles del baby shower frente al arco de globos"),
    ],
  },
];

/** Entrega de una ruta, o null si la ruta no es una entrega. */
export const entregaPorPath = (pathname) =>
  ENTREGAS.find((entrega) => entrega.path === pathname) || null;
